/** /bus-stops — cross-tenant Vehicle_Route_Stop catalog (grid).
 *
 * Columns: name → RouterLink, label, tenant, enabled, has_image,
 * attraction_count, updated_ts. Filters: tenant scope + free-text search. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ImageIcon from '@mui/icons-material/Image';
import { Box, Button, CircularProgress } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as routeStopsApi from '@/api/routeStops';
import type { RouteStopListItem } from '@/api/routeStops';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { formatRelativeTime } from '@/utils/format';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { BusStopCreateDialog } from './BusStopCreateDialog';

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);
const DEFAULT_ORDER = [
  'id',
  'name',
  'label',
  'tenant',
  'enabled',
  'has_image',
  'attraction_count',
  'warning',
  'description',
  'updated_ts',
];

/** Columns present but hidden until toggled on via the column menu. */
const DEFAULT_HIDDEN = ['description', 'location'];

export function BusStopsListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_fleet_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'route_stops',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what a create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());

  const columns = useMemo<GridColDef<RouteStopListItem>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/bus-stops/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'label',
        headerName: 'Label',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.label ?? '',
        renderCell: (p) => p.row.label || '—',
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        sortable: false,
        renderCell: (p) => (p.row.enabled ? <CheckIcon fontSize="small" color="success" /> : '—'),
      },
      {
        field: 'has_image',
        headerName: 'Image',
        width: 90,
        sortable: false,
        renderCell: (p) => (p.row.has_image ? <ImageIcon fontSize="small" color="action" /> : '—'),
      },
      {
        // lat/lng are already serialized but had no column. Render coords + a Google
        // Maps link (external <a> to avoid coupling to the fleet feature's helper).
        field: 'location',
        headerName: 'Location',
        width: 170,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) =>
          row.latitude != null && row.longitude != null ? `${row.latitude},${row.longitude}` : '',
        renderCell: (p) =>
          p.row.latitude != null && p.row.longitude != null ? (
            <a
              href={`https://www.google.com/maps?q=${p.row.latitude},${p.row.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            >
              {p.row.latitude.toFixed(4)}, {p.row.longitude.toFixed(4)}
            </a>
          ) : (
            '—'
          ),
      },
      {
        field: 'attraction_count',
        headerName: 'Attractions',
        width: 120,
        sortable: false,
        renderCell: (p) => p.row.attraction_count,
      },
      {
        field: 'warning',
        headerName: 'Warning',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => row.warning ?? '',
        renderCell: (p) => p.row.warning || '—',
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.description ?? '',
        renderCell: (p) => p.row.description || '—',
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 150,
        sortable: true,
        valueGetter: (_, row) => row.updated_ts ?? '',
        renderCell: (p) => (p.row.updated_ts ? formatRelativeTime(p.row.updated_ts) : '—'),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      routeStopsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="bus-stops-line-sets"
        title="Bus stops"
        subtitle="Cross-tenant bus-stop catalog."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New bus stop
            </Button>
          )
        }
      />

      <BusStopCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<RouteStopListItem>
          queryKey={['route_stops']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="route_stops"
          batchActions={[deleteBatchAction('console_fleet_manage')]}
          defaultSort={{ field: 'name', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name or label',
            exportFileName: 'bus-stops',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
